import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { clearToken, getToken, setToken } from "@/lib/api";
import { isNativeApp, registerNativePush } from "@/lib/nativePush";

const AuthContext = createContext(null);
const OFFICE_ROLES = ["owner", "manager", "dispatcher", "accountant"];
const pushScope = (role) =>
  role === "chauffeur" ? "driver" : role === "client" ? "client" : "office";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshQuota = useCallback(async () => {
    try {
      const { data } = await api.get("/quota");
      setQuota(data);
    } catch {
      setQuota(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      if (!getToken()) throw new Error("no session");
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setCompany(data.company);
      if (data.company && OFFICE_ROLES.includes(data.user?.role)) refreshQuota();
      return data;
    } catch {
      setUser(false);
      setCompany(null);
      setQuota(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [refreshQuota]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyAuth = (data) => {
    if (data.token) setToken(data.token);
    setUser(data.user);
    setCompany(data.company);
    if (data.company && OFFICE_ROLES.includes(data.user?.role)) refreshQuota();
    setLoading(false);
    // Store builds register this device for native push once there is someone to notify.
    if (isNativeApp() && data.user?.role) registerNativePush(pushScope(data.user.role));
  };

  const login = async (payload) => {
    const { data } = await api.post("/auth/login", payload);
    applyAuth(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    applyAuth(data);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout call failed; clearing this app's session anyway", e);
    }
    clearToken();
    setUser(false);
    setCompany(null);
    setQuota(null);
  };

  const hasFlag = (flag) => Boolean(company?.flags?.includes(flag));
  const can = (permission) =>
    user?.role === "owner" || Boolean(user?.permissions?.includes(permission));

  return (
    <AuthContext.Provider
      value={{ user, company, quota, loading, login, register, logout, refresh, refreshQuota,
               setCompany, hasFlag, can, applySession: applyAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
